import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ShieldCheck, Clock, AlertTriangle, LogOut, Loader2, Send } from "lucide-react";
import { ClinqLogo } from "../components/ClinqLogo";

export default function FunderPendingApproval() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const status = user?.funder_approval_status || "pending";
  const rejectionReason = user?.rejection_reason || "";

  const [note, setNote] = useState("");
  const [organization, setOrganization] = useState(user?.organization || "");
  const [website, setWebsite] = useState(user?.organization_website || "");
  const [focus, setFocus] = useState(user?.investment_focus || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  // Funder reached this page after their mandate was confirmed and they have
  // an active session; if they are actually approved already, kick them on.
  if (status !== "pending" && status !== "rejected") {
    nav("/funder/terminal", { replace: true });
    return null;
  }

  const reapply = async () => {
    setBusy(true);
    setMsg("");
    try {
      await api.post("/auth/funder/reapply", {
        note: note.trim(),
        organization: organization.trim(),
        website: website.trim(),
        focus: focus.trim(),
      });
      await refresh();
      setMsg("Application re-submitted. We'll notify you once an admin reviews it.");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Couldn't re-submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <header className="border-b border-neutral-900 px-6 py-4">
        <div className="mx-auto flex max-w-5xl items-center justify-between">
          <ClinqLogo />
          <button
            onClick={logout}
            data-testid="funder-pending-logout"
            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-xs font-semibold text-neutral-300 hover:bg-neutral-800"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-12">
        {status === "pending" && (
          <div
            data-testid="funder-pending-card"
            className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-8"
          >
            <div className="flex items-start gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/15 text-amber-300">
                <Clock className="h-6 w-6" />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-amber-300">
                  Awaiting approval
                </p>
                <h1 className="mt-1 text-2xl font-bold text-neutral-100">
                  Your funder account is under review
                </h1>
                <p className="mt-3 text-sm leading-relaxed text-neutral-300">
                  Welcome, <b>{user?.name || user?.email}</b>. To protect founders&apos;
                  confidential data, every new funder is reviewed by a Clinq admin
                  before getting access to the terminal. We typically respond
                  within <b>1 business day</b>.
                </p>
                <p className="mt-3 text-sm text-neutral-400">
                  We&apos;ll send a notification to <b className="text-neutral-200">{user?.email}</b> once
                  you&apos;re approved. You can sign back in any time.
                </p>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Step icon={ShieldCheck} label="1. Application received" done />
              <Step icon={Clock} label="2. Admin review in progress" active />
              <Step icon={Send} label="3. You&apos;ll get terminal access" />
            </div>
          </div>
        )}

        {status === "rejected" && (
          <div
            data-testid="funder-rejected-card"
            className="rounded-2xl border border-red-500/30 bg-red-500/5 p-8"
          >
            <div className="flex items-start gap-4">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-500/15 text-red-300">
                <AlertTriangle className="h-6 w-6" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold uppercase tracking-wider text-red-300">
                  Application not approved
                </p>
                <h1 className="mt-1 text-2xl font-bold text-neutral-100">
                  Your funder application wasn&apos;t approved
                </h1>
                {rejectionReason ? (
                  <div className="mt-3 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-100">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-red-300">Reviewer note</p>
                    <p className="mt-1" data-testid="funder-rejection-reason">{rejectionReason}</p>
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-neutral-300">
                    The admin team didn&apos;t approve your initial application. You&apos;re
                    welcome to update your details below and re-apply — we&apos;ll review
                    again.
                  </p>
                )}
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <Field label="Organization">
                <input
                  data-testid="funder-reapply-organization"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  placeholder="e.g. Sahara Ventures"
                  className={inputCls}
                />
              </Field>
              <Field label="Organization website">
                <input
                  data-testid="funder-reapply-website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://"
                  className={inputCls}
                />
              </Field>
              <Field label="Investment focus">
                <input
                  data-testid="funder-reapply-focus"
                  value={focus}
                  onChange={(e) => setFocus(e.target.value)}
                  placeholder="e.g. Series A fintech in West Africa, $250K-$2M"
                  className={inputCls}
                />
              </Field>
              <Field label="Note for the admin (optional)">
                <textarea
                  data-testid="funder-reapply-note"
                  rows={4}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add any extra context that might help your re-application."
                  className={inputCls}
                />
              </Field>
            </div>
            <div className="mt-5 flex items-center justify-between gap-3">
              {msg && (
                <p data-testid="funder-reapply-msg" className="text-sm text-neutral-300">
                  {msg}
                </p>
              )}
              <button
                onClick={reapply}
                disabled={busy}
                data-testid="funder-reapply-submit"
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-[#F59E0B] px-5 py-2 text-sm font-bold text-neutral-950 hover:bg-[#EAB308] disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {busy ? "Re-submitting…" : "Re-apply for approval"}
              </button>
            </div>
          </div>
        )}

        {status === "pending" && msg && (
          <p data-testid="funder-pending-msg" className="mt-4 text-sm text-emerald-300">
            {msg}
          </p>
        )}
      </main>
    </div>
  );
}

const inputCls =
  "w-full rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2.5 text-sm text-neutral-100 outline-none focus:border-[#F59E0B]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-neutral-400">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Step({ icon: Icon, label, done, active }) {
  return (
    <div
      className={
        "flex items-center gap-2 rounded-lg border px-3 py-2 text-xs " +
        (done
          ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-200"
          : active
          ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
          : "border-neutral-800 bg-neutral-900 text-neutral-500")
      }
    >
      <Icon className="h-4 w-4" />
      <span className="font-semibold">{label}</span>
    </div>
  );
}
